import { analyticsTeamsService } from "@/services/analytics-teams-service";
import type { TeamsAvgTimeSeriesRow } from "@/types/analytics-teams";
import { downloadBlob } from "@/utils/downloadFile";
import {
  downloadAnalyticsExport,
  type AnalyticsExportTableSection,
} from "../../utils/analyticsFileExport";
import { TEAMS_TOP_LIMIT } from "./constants";
import type { TeamSegment, TeamStaffRow, TeamSummary } from "./types";

export type TeamsExportParams = {
  hotelId: string;
  from: string;
  to: string;
  format: "csv" | "xlsx";
};

export interface TeamsTabExportLabels {
  summaryTitle: string;
  metric: string;
  value: string;
  performanceTitle: string;
  rank: string;
  name: string;
  tasksCompleted: string;
  tasksCancelled: string;
  avgTimeOnTask: string;
  delays: string;
  dayOff: string;
  dailyAverageTitle: string;
  day: string;
  taskCount: string;
  avgSeconds: string;
}

interface TeamsTabExportParams extends TeamsExportParams {
  segment: TeamSegment;
  summary: TeamSummary;
  rows: TeamStaffRow[];
  avgTimeSeries: TeamsAvgTimeSeriesRow[];
  labels: TeamsTabExportLabels;
}

function buildSummarySection(
  params: TeamsTabExportParams,
): AnalyticsExportTableSection {
  const { labels, segment, summary } = params;
  const rows: AnalyticsExportTableSection["rows"] = [
    [labels.tasksCompleted, summary.tasksCompleted],
    [labels.tasksCancelled, summary.tasksCancelled],
    [labels.avgTimeOnTask, summary.avgTimeOnTask],
  ];

  if (segment === "staff") {
    rows.push(
      [labels.delays, summary.delays],
      [labels.dayOff, summary.daysOff],
    );
  }

  return {
    title: labels.summaryTitle,
    headers: [labels.metric, labels.value],
    rows,
  };
}

function buildPerformanceSection(
  params: TeamsTabExportParams,
): AnalyticsExportTableSection {
  const { labels, rows, segment } = params;
  const headers = [
    labels.rank,
    labels.name,
    labels.tasksCompleted,
    labels.tasksCancelled,
    labels.avgTimeOnTask,
  ];

  if (segment === "staff") {
    headers.push(labels.delays, labels.dayOff);
  }

  return {
    title: labels.performanceTitle,
    headers,
    rows: rows.map((row) => {
      const values: AnalyticsExportTableSection["rows"][number] = [
        row.rank,
        row.name,
        row.tasksCompleted,
        row.tasksCancelled,
        row.avgTimeOnTask,
      ];

      if (segment === "staff") {
        values.push(row.delays, row.daysOff);
      }

      return values;
    }),
  };
}

function buildDailyAverageSection(
  params: TeamsTabExportParams,
): AnalyticsExportTableSection {
  const { avgTimeSeries, labels } = params;

  return {
    title: labels.dailyAverageTitle,
    headers: [
      labels.day,
      labels.taskCount,
      labels.avgSeconds,
      labels.avgTimeOnTask,
    ],
    rows: avgTimeSeries.map((row) => [
      row.day,
      row.taskCount,
      row.avgSeconds,
      row.humanReadable,
    ]),
  };
}

export async function downloadTeamsOverviewExport(
  params: TeamsExportParams,
): Promise<void> {
  const blob = await analyticsTeamsService.exportOverview(params);
  downloadBlob(
    blob,
    `teams-overview-${params.hotelId}-${params.from}-${params.to}.${params.format}`,
  );
}

export async function downloadTeamsTopExport(
  params: TeamsExportParams & { entity: "STAFF" | "TEAM" },
): Promise<void> {
  const blob = await analyticsTeamsService.exportTop({
    ...params,
    entity: params.entity,
    limit: TEAMS_TOP_LIMIT,
  });
  const suffix = params.entity === "STAFF" ? "staff" : "team";
  downloadBlob(
    blob,
    `teams-top-${suffix}-${params.hotelId}-${params.from}-${params.to}.${params.format}`,
  );
}

export async function downloadTeamsTabExport(
  params: TeamsTabExportParams,
): Promise<void> {
  downloadAnalyticsExport(
    params.format,
    [
      buildSummarySection(params),
      buildPerformanceSection(params),
      buildDailyAverageSection(params),
    ],
    `analytics-team-${params.segment}-${params.hotelId}-${params.from}-${params.to}`,
  );
}

export async function downloadTeamsDetailExport(
  params: TeamsExportParams & { segment: TeamSegment },
): Promise<void> {
  await downloadTeamsOverviewExport(params);
  await downloadTeamsTopExport({
    ...params,
    entity: params.segment === "teams" ? "TEAM" : "STAFF",
  });
}
